<template>
  <div>
    <header-component />
    <div class="container-fluid mt--6">

      <!-- Branch info bar -->
      <div class="card mb-3">
        <div class="card-body py-3 d-flex align-items-center justify-content-between flex-wrap" style="gap:12px">
          <h4 class="mb-0">{{ branchName || 'Branch' }} — Ledger</h4>
          <div class="d-flex align-items-center" style="gap:16px">
            <div class="text-right">
              <div class="text-xs text-muted" style="font-size:0.75rem">Total Balance</div>
              <div class="font-weight-bold" style="font-size:1.1rem" :class="balance >= 0 ? 'text-success' : 'text-danger'">
                {{ balance | currency }}
              </div>
            </div>
            <button class="btn btn-sm btn-outline-secondary" @click="$router.back()">← Back</button>
          </div>
        </div>
      </div>

      <!-- Transactions table -->
      <div class="card">
        <div class="card-header d-flex align-items-center justify-content-between">
          <h3 class="mb-0">Transaction History</h3>
          <span v-if="loading" class="text-muted small">Loading…</span>
        </div>

        <div class="table-responsive">
          <table class="table align-items-center table-flush mb-0">
            <thead class="thead-light">
              <tr>
                <th style="width:150px">Date</th>
                <th style="width:90px">Type</th>
                <th style="width:130px">Amount</th>
                <th>Description</th>
                <th style="width:200px">Reference</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading && transactions.length === 0">
                <td colspan="5" class="text-center text-muted py-4">Loading…</td>
              </tr>
              <tr v-else-if="!loading && transactions.length === 0">
                <td colspan="5" class="text-center text-muted py-4">No transactions found.</td>
              </tr>
              <tr v-for="tx in transactions" :key="tx.id">
                <td class="text-muted small" style="white-space:nowrap">
                  {{ tx.created_at | formatDate }}
                </td>
                <td>
                  <span
                    class="badge"
                    :class="tx.type === 'credit' ? 'badge-success' : 'badge-danger'"
                    style="font-size:0.78rem;padding:3px 8px"
                  >
                    {{ tx.type === 'credit' ? 'Credit' : 'Debit' }}
                  </span>
                </td>
                <td class="font-weight-bold" :class="tx.type === 'credit' ? 'text-success' : 'text-danger'">
                  {{ tx.type === 'credit' ? '+' : '-' }}{{ tx.amount | currency }}
                </td>
                <td>
                  <div style="max-width:380px">{{ tx.description || '—' }}</div>
                </td>
                <td>
                  <!-- Sale payment: link to the sale -->
                  <div v-if="tx.reference_type === 'sale' && tx.reference_id">
                    <span class="badge badge-light text-secondary mr-1" style="font-size:0.72rem">Sale</span>
                    <router-link
                      :to="'/pos/' + tx.reference_id + '/'"
                      class="text-primary small"
                      style="text-decoration:underline"
                    >
                      View Sale ↗
                    </router-link>
                  </div>
                  <!-- Credit note (cash refund): link to the originating sale -->
                  <div v-else-if="tx.reference_type === 'credit_note' && tx.reference_id">
                    <span class="badge badge-danger mr-1" style="font-size:0.72rem;opacity:0.8">Refund</span>
                    <router-link
                      :to="'/pos/' + tx.reference_id + '/'"
                      class="text-danger small"
                      style="text-decoration:underline"
                    >
                      View Sale ↗
                    </router-link>
                  </div>
                  <!-- Advance: customer name parsed from description -->
                  <div v-else-if="tx.reference_type === 'advance'">
                    <span class="badge badge-light text-secondary mr-1" style="font-size:0.72rem">Advance</span>
                    <span class="small text-muted">{{ tx.description | customerFromDesc }}</span>
                  </div>
                  <span v-else-if="tx.reference_type" class="text-muted small text-capitalize">
                    {{ tx.reference_type }}
                  </span>
                  <span v-else class="text-muted">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="card-footer">
          <div
            v-if="meta"
            class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
          >
            <p class="card-category mb-0">
              Showing {{ meta.from || 0 }} to {{ meta.to || 0 }} of {{ meta.total || 0 }} entries
            </p>
            <pagination :meta="paginationMeta" @paginate="setPagination" />
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import headerComponent from "../Components/HeaderComponent";
import pagination from "@/components/AmsaPagination";
import Api from "@/middleware/api";
import endpoints from "@/utils/constants";

export default {
  name: "BranchLedger",
  components: {
    "header-component": headerComponent,
    pagination,
  },
  filters: {
    currency(value) {
      const amount = parseFloat(value) || 0;
      return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(amount);
    },
    formatDate(value) {
      if (!value) return "—";
      return new Date(value).toLocaleString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit"
      });
    },
    customerFromDesc(desc) {
      if (!desc) return "";
      const match = desc.match(/customer:\s*(.+)$/i);
      return match ? match[1].trim() : "";
    },
  },
  data() {
    return {
      branchName: "",
      balance: 0,
      transactions: [],
      meta: null,
      loading: false,
      currentPage: 1,
      perPage: 20,
    };
  },
  computed: {
    branchId() {
      return this.$route.params.branchId;
    },
    paginationMeta() {
      if (!this.meta) return null;
      return {
        from: this.meta.from,
        to: this.meta.to,
        total: this.meta.total,
        perPage: this.meta.per_page,
        currentPage: this.meta.current_page,
        last: this.meta.last_page,
      };
    },
  },
  mounted() {
    this.fetchLedger();
  },
  methods: {
    fetchLedger(page = 1) {
      this.loading = true;
      new Api()
        .call(
          "get",
          endpoints.LARAVEL_BASE_URL +
            "/api/v1/branches/" + this.branchId + "/transactions" +
            "?page=" + page + "&perPage=" + this.perPage
        )
        .then(res => {
          this.branchName = res.data.branch_name || "";
          this.balance = res.data.balance || 0;
          const t = res.data.transactions;
          this.transactions = t.data || [];
          this.meta = t;
        })
        .catch(() => {
          this.$notify({ message: "Failed to load ledger.", type: "danger", title: "Error" });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    setPagination(data) {
      this.perPage = data.perPage;
      this.currentPage = data.page;
      this.fetchLedger(data.page);
    },
  },
};
</script>
